import type {ReactElement} from 'react';
import {AnimationStylesPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/animation-styles-panel/animation-styles-panel';
import {useAppSelector} from '@/hooks';
import {getCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import type {AnimationSpeed, SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';

export function PageOutroAnimationPanel(): ReactElement<any> | null {
  const currentPageObject = useAppSelector(getCurrentPage);

  if (!currentPageObject) {
    throw new Error('Unable to show animation options since the current page is null');
  }

  if (Object.keys(currentPageObject.items.itemsHashMap).length === 0) {
    return null;
  }

  const onAnimationTypeModified = (animationType: AnimationType): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    trackPosterBuilderGA4Events(GA4EventName.ANIMATION_DESIGN_EXIT, {[GA4EventParamName.ANIMATION_TYPE]: animationType});
    currentPage.outroAnimation.updateFromObject(
      {
        type: animationType,
        direction: animationType === AnimationType.ZOOM ? PositionType.LEFT : PositionType.RIGHT,
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };
  const onAnimationSpeedModified = (undoable: boolean, replayPosterOnChange: boolean, speed: AnimationSpeed): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    currentPage.outroAnimation.updateFromObject(
      {
        speed: speed,
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: replayPosterOnChange, undoable: undoable}
    );
  };
  const onAnimationDirectionModified = (direction: SlideType | PositionType): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      throw new Error('Unable to change animation type since current page is undefined');
    }

    currentPage.outroAnimation.updateFromObject(
      {
        direction: direction,
      },
      {checkForDurationUpdate: true, replayPosterOnUpdateDone: true}
    );
  };

  const getAnimations = (): AnimationType[] => {
    return [
      AnimationType.NONE,
      AnimationType.BLOCK,
      AnimationType.FADE,
      AnimationType.JELLO,
      AnimationType.PAN,
      AnimationType.POP,
      AnimationType.ROTATE,
      AnimationType.SCALE,
      AnimationType.SLIDE,
      AnimationType.WIPE,
      AnimationType.PEEK,
      AnimationType.ZOOM,
    ];
  };

  return (
    <AnimationStylesPanel
      selectedAnimation={currentPageObject.outroAnimation.type}
      onAnimationTypeModified={onAnimationTypeModified}
      onAnimationSpeedModified={onAnimationSpeedModified}
      onAnimationDirectionModified={onAnimationDirectionModified}
      speed={currentPageObject.outroAnimation.speed}
      direction={currentPageObject.outroAnimation.direction}
      animations={getAnimations()}
      showInGrid={true}
    />
  );
}
